import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { SessionResponse } from "../../shared/types";
import { TurnstileWidget } from "../components/TurnstileWidget";
import { login } from "../lib/api";
import { formatCooldownMessage, getEmailCooldownRemainingMs, startEmailCooldown, useEmailCooldown } from "../lib/email-cooldowns";

export function LoginPage(props: { refreshSession: () => Promise<void>; session: SessionResponse["user"] }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>();
  const [message, setMessage] = useState<string | null>(null);
  const verificationCooldownMs = useEmailCooldown("verification", email);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const remainingCooldownMs = getEmailCooldownRemainingMs("verification", email);
    if (remainingCooldownMs > 0) {
      setMessage(formatCooldownMessage(remainingCooldownMs));
      return;
    }

    try {
      await login(email, password, turnstileToken);
      await props.refreshSession();
      navigate("/");
    } catch (error) {
      const nextMessage = error instanceof Error ? error.message : "Could not sign in.";
      if (nextMessage.includes("We just sent you a fresh verification link")) {
        startEmailCooldown("verification", email);
      }
      setMessage(nextMessage);
    }
  }

  return (
    <section className="form-panel auth-panel">
      <div className="section-heading">
        <p className="eyebrow">Log in</p>
        <h1 className="compact-title">Welcome back</h1>
      </div>
      <form className="auth-form" onSubmit={handleSubmit}>
        <label>
          Dartmouth email
          <input required type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
        </label>
        <label>
          Password
          <input required type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
        </label>
        <TurnstileWidget onToken={setTurnstileToken} />
        <button type="submit" className="primary-link" disabled={verificationCooldownMs > 0}>
          {verificationCooldownMs > 0 ? `Wait ${Math.ceil(verificationCooldownMs / 1000)}s` : "Log in"}
        </button>
      </form>
      <p className="auth-copy">
        <Link to="/forgot-password/">Forgot your password?</Link>
      </p>
      {message ? <p className="status-message" role="status" aria-live="polite">{message}</p> : null}
    </section>
  );
}
